import { MaritalStatus } from '@egw/api'
import { Button, Field, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { type FC, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

// Component to be used within a FormProvider from react-hook-form
export const EditMaritalStatus: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { openModal, closeModal, isOpen } = useModalState()
  const [defaultMaritalStatus, setDefaultMaritalStatus] = useState<MaritalStatus | undefined>(undefined)

  const { control, watch, setValue } = useFormContext()

  const maritalStatus = watch('maritalStatus') as MaritalStatus | undefined

  useEffect(() => {
    if (maritalStatus !== undefined && defaultMaritalStatus === undefined) {
      setDefaultMaritalStatus(maritalStatus)
    }
  }, [maritalStatus, defaultMaritalStatus])

  // Closes the modal and reverts the value to the default value
  // Only using the closeModal method will persist the value
  const closeWithoutSaving = (): void => {
    setValue('maritalStatus', defaultMaritalStatus)
    closeModal()
  }

  // Sets the new default value for future edits
  const closeWithSaving = (): void => {
    setDefaultMaritalStatus(maritalStatus)
    closeModal()
  }

  return (
    <>
      <Field
        className="border-b py-sm"
        valueClassName="text-text-meta typo-primary-base-regular"
        value={t('edit_fields.marital_status_title')}
        rightValue={maritalStatus ? t(`marital_status.${maritalStatus}`) : t('common.not_specified')}
        testId="editMaritalStatus"
        onEdit={openModal}
      />
      <ModalDrawer isOpen={isOpen} onClose={closeWithoutSaving} title={t('edit_fields.edit_marital_status')}>
        <>
          <Controller
            name="maritalStatus"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { ref: _, ...restField } }) => (
              <RadioGroup
                {...restField}
                options={Object.values(MaritalStatus).map((item) => ({
                  value: item,
                  label: t(`marital_status.${item}`),
                }))}
                name="maritalStatus"
                className="pb-md typo-primary-base"
                testId="marital-status-radio"
              />
            )}
          />
          <Button size="small" onClick={closeWithSaving} className="w-full mt-lg" testId="primaryAction_button">
            {t('common.confirm')}
          </Button>
        </>
      </ModalDrawer>
    </>
  )
}
